import { useModalDialog } from "../hooks/useModalDialog";
import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Navbar from '../components/Navbar';
import HlcFooter from '../components/HlcFooter';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { getProductImage, getRealImageUrl } from '../utils/getProductImage';
import { API_BASE_URL } from '../config/apiConfig';
import { addToCart, incrementQty, decrementQty } from '../redux/cartSlice';
import { cssVars } from '../theme';
import QuickViewModal from '../components/shop/QuickViewModal';
import BulbCutout from '../assets/products/topseller-cutouts/3.png';
import DownlightCutout from '../assets/products/topseller-cutouts/9.png';
import PanelCutout from '../assets/products/topseller-cutouts/28.png';
import FloodCutout from '../assets/products/topseller-cutouts/31.png';
import RopeCutout from '../assets/products/topseller-cutouts/35.png';

/* Presentation-only transparent cutouts (already generated for the
   homepage Top Selling section) — reused here so products float on the
   dark card instead of showing their baked-in light backgrounds. Mirrors
   the same keyword logic as utils/getProductImage.js; that shared util
   itself is untouched. The live catalog's categories (downlight, street
   & flood, rope, panel, bulb) map 1:1 onto these 5 files — verified
   against the live API before building this. */
const CUTOUT_MAP = [
  { keywords: ['bulb', 'candle'], image: BulbCutout },
  { keywords: ['downlight', 'down light', 'cob', 'smd', 'ice light'], image: DownlightCutout },
  { keywords: ['batten', 'tube'], image: DownlightCutout },
  { keywords: ['panel'], image: PanelCutout },
  { keywords: ['flood', 'street'], image: FloodCutout },
  { keywords: ['rope', 'strip', 'decorative'], image: RopeCutout },
];

function getCutoutImage(item) {
  if (!item) return BulbCutout;
  // A real uploaded image always wins over the local cutout fallback.
  const realUrl = getRealImageUrl(item);
  if (realUrl) return realUrl;
  const haystack = [item.category, item.subcategory, item.name, item.title, item.sub, item.product]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  const match = CUTOUT_MAP.find(({ keywords }) => keywords.some((kw) => haystack.includes(kw)));
  return match ? match.image : getProductImage(item);
}

// Real-data-only badge, matching the shared ProductCard's priority.
function resolveBadge(product) {
  if (product.discount) return product.discount;
  if (product.topSelling) return "Best Seller";
  if (product.featured) return "Popular";
  return null;
}

const PAGE_SIZE = 12;

const SORT_OPTIONS = [
  { value: 'featured', label: 'Featured' },
  { value: 'price-asc', label: 'Price: Low to High' },
  { value: 'price-desc', label: 'Price: High to Low' },
  { value: 'name-asc', label: 'Name: A-Z' },
];

const BagIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M6 7h12l-1 13H7L6 7z" /><path d="M9 7a3 3 0 0 1 6 0" /></svg>
);
const EyeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M1.5 12S5 5 12 5s10.5 7 10.5 7-3.5 7-10.5 7S1.5 12 1.5 12z" /><circle cx="12" cy="12" r="3" /></svg>
);
const FilterIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 6h16M7 12h10M10 18h4" /></svg>
);
const ChevronIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6" /></svg>
);
const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
);

export default function HLCShop() {
  const location = useLocation();
  // Category to pre-filter by when arriving from another page (e.g. a
  // Lighting Guide's "Browse Products" button) with navigate("/shop",
  // { state: { category } }). Reuses the existing filter system below
  // instead of a separate filtered view.
  const incomingCategory = location.state?.category || '';

  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const [filterMainCategory, setFilterMainCategory] = useState('');
  const [filterProduct, setFilterProduct] = useState('');
  const [filterWatt, setFilterWatt] = useState('');

  const [sortBy, setSortBy] = useState('featured');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const { dialogRef, isModal } = useModalDialog(mobileFiltersOpen, () => setMobileFiltersOpen(false), "(max-width: 991px)");

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const cartItems = useSelector((state) => state.cart.items);
  const getCartItem = (id) => cartItems.find((item) => item.id === id);

  /* ---------------- FETCH PRODUCTS: adminhlc-api (MySQL) — unchanged ---------------- */
  useEffect(() => {
    let cancelled = false;

    fetch(`${API_BASE_URL}/api/public/products/list.php`)
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        const parsed = Array.isArray(data) ? data : [];
        setProducts(parsed);
        setFilteredProducts(parsed);
      })
      .catch((err) => {
        console.error("Failed to fetch products:", err);
        if (!cancelled) {
          setProducts([]);
          setFilteredProducts([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  /* ---------------- PRE-FILTER FROM INCOMING NAVIGATION STATE — unchanged ---------------- */
  useEffect(() => {
    if (!incomingCategory || products.length === 0) return;
    setFilterMainCategory(incomingCategory);
    setFilteredProducts(
      products.filter(
        (p) => (p.category || 'Uncategorized').toLowerCase() === incomingCategory.toLowerCase()
      )
    );
  }, [incomingCategory, products]);

  /* ---------------- CASCADING TREE — unchanged ---------------- */
  const TREE = useMemo(() => {
    const tree = {};

    products.forEach((item) => {
      const main = item.category || 'Uncategorized';
      const prod = item.name || '';
      const watts = Array.isArray(item.wattColourModel) ? item.wattColourModel : [];

      if (!tree[main]) tree[main] = {};
      if (!tree[main][prod]) tree[main][prod] = new Set();

      watts.forEach((w) => tree[main][prod].add(w));
    });

    const result = {};
    Object.keys(tree).sort().forEach((main) => {
      result[main] = {};
      Object.keys(tree[main]).sort().forEach((prod) => {
        result[main][prod] = [...tree[main][prod]].sort();
      });
    });

    return result;
  }, [products]);

  const mainList = Object.keys(TREE);
  const productsList = filterMainCategory ? Object.keys(TREE[filterMainCategory] || {}) : [];
  const wattList = filterMainCategory && filterProduct ? TREE[filterMainCategory]?.[filterProduct] || [] : [];

  /* ---------------- SORT (new — genuinely functional client-side sort
     over the already-fetched/filtered data; "Featured" = original API
     order, nothing invented beyond what the data already supports) ---- */
  const sortedProducts = useMemo(() => {
    const arr = [...filteredProducts];
    const priceOf = (p) => Number(p.discountedPrice || p.price) || 0;
    switch (sortBy) {
      case 'price-asc': return arr.sort((a, b) => priceOf(a) - priceOf(b));
      case 'price-desc': return arr.sort((a, b) => priceOf(b) - priceOf(a));
      case 'name-asc': return arr.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      default: return arr;
    }
  }, [filteredProducts, sortBy]);

  /* ---------------- LOAD MORE (new — reveals already-fetched products
     in batches client-side, keeps the initial view dense/scannable) ---- */
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [filteredProducts, sortBy]);

  const visibleProducts = sortedProducts.slice(0, visibleCount);
  const hasMore = visibleCount < sortedProducts.length;

  /* ---------------- HANDLERS — unchanged ---------------- */
  const handleMainChange = (e) => {
    setFilterMainCategory(e.target.value);
    setFilterProduct('');
    setFilterWatt('');
  };

  const handleProductChange = (e) => {
    const value = e.target.value;
    setFilterProduct(value);
    setFilterWatt('');

    const watts = TREE[filterMainCategory]?.[value] || [];
    if (watts.length > 0) setFilterWatt(watts[0]);
  };

  const handleWattChange = (e) => setFilterWatt(e.target.value);

  const applyFilters = () => {
    let temp = [...products];

    if (filterMainCategory) {
      temp = temp.filter(
        (p) =>
          (p.category || 'Uncategorized').toLowerCase() ===
          filterMainCategory.toLowerCase()
      );
    }

    if (filterProduct) {
      temp = temp.filter(
        (p) => (p.name || '').toLowerCase() === filterProduct.toLowerCase()
      );
    }

    if (filterWatt) {
      temp = temp.filter((p) => {
        if (!p.wattColourModel || !Array.isArray(p.wattColourModel)) return false;
        return p.wattColourModel.some(
          (w) =>
            String(w).trim().toLowerCase() ===
            String(filterWatt).trim().toLowerCase()
        );
      });
    }

    setFilteredProducts(temp);
    setMobileFiltersOpen(false);
  };

  const clearFilters = () => {
    setFilterMainCategory('');
    setFilterProduct('');
    setFilterWatt('');
    setFilteredProducts(products);
  };

  const openModal = (product) => {
    setSelectedProduct(product);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedProduct(null);
  };

  const viewProduct = (productId) => navigate(`/product/${productId}`);

  const activeFilterCount = [filterMainCategory, filterProduct, filterWatt].filter(Boolean).length;

  /* ---------------- FILTER PANEL (shared between desktop sidebar and
     mobile drawer — same markup, positioned differently via CSS) ------- */
  const filterPanel = (
    <>
      <div className="hlc-shop__filters-head">
        <h3>
          Filters
          {activeFilterCount > 0 && <span className="filter-badge">{activeFilterCount}</span>}
        </h3>
        <button type="button" className="hlc-shop__filters-close" onClick={() => setMobileFiltersOpen(false)} aria-label="Close filters">
          <CloseIcon />
        </button>
      </div>

      <label htmlFor="shop_mainCategory">Category</label>
      <div className="hlc-shop__select-wrap">
        <select
          id="shop_mainCategory"
          value={filterMainCategory}
          onChange={handleMainChange}
          className={filterMainCategory ? 'has-value' : ''}
        >
          <option value="">All Categories</option>
          {mainList.map((main) => (
            <option key={main} value={main}>{main}</option>
          ))}
        </select>
        <ChevronIcon />
      </div>

      {filterMainCategory && (
        <>
          <label htmlFor="shop_product">Product</label>
          <div className="hlc-shop__select-wrap">
            <select
              id="shop_product"
              value={filterProduct}
              onChange={handleProductChange}
              className={filterProduct ? 'has-value' : ''}
            >
              <option value="">All Products</option>
              {productsList.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
            <ChevronIcon />
          </div>
        </>
      )}

      {filterProduct && (
        <>
          <label htmlFor="shop_watt">Watt / Model</label>
          <div className="hlc-shop__select-wrap">
            <select
              id="shop_watt"
              value={filterWatt}
              onChange={handleWattChange}
              className={filterWatt ? 'has-value' : ''}
            >
              <option value="">All Watts / Models</option>
              {wattList.map((w) => (
                <option key={w} value={w}>{w}</option>
              ))}
            </select>
            <ChevronIcon />
          </div>
        </>
      )}

      <div className="filter-actions">
        <button type="button" id="applyFilters" onClick={applyFilters}>Apply Filters</button>
        <button type="button" id="clearFilters" onClick={clearFilters}>Clear All</button>
      </div>
    </>
  );

  return (
    <>
      <style>{`
        :root {
          --hlc-magenta: #9b1f6c;
          --hlc-plum: #542757;
          --hlc-navy: #35335b;
          --hlc-line: #eceaf1;
        }

        #hlc-shop-page, #hlc-shop-page * { box-sizing: border-box; }

        #hlc-shop-page {
          font-family: "Open Sans", system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif;
          background: var(--hlc-gradient-bg);
          color: var(--hlc-text);
        }

        /* ---------- hero ---------- */
        .hlc-shop__hero {
          padding: clamp(22px, 3vw, 32px) 0 clamp(14px, 2vw, 20px);
        }
        .hlc-shop__hero-inner {
          width: min(1360px, 94vw);
          margin: 0 auto;
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 24px;
          flex-wrap: wrap;
        }
        .hlc-shop__hero-main { flex: 1 1 480px; min-width: 0; }
        .hlc-shop__eyebrow {
          display: block;
          margin: 0 0 8px;
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 0.24em;
          text-transform: uppercase;
          color: var(--hlc-magenta-light);
        }
        .hlc-shop__hero h1 {
          margin: 0 0 8px;
          font-size: clamp(24px, 3.4vw, 34px);
          font-weight: 800;
          letter-spacing: -0.01em;
          line-height: 1.15;
        }
        .hlc-shop__hero p {
          margin: 0 0 18px;
          max-width: 640px;
          font-size: 14px;
          line-height: 1.55;
          color: var(--hlc-text-secondary);
        }

        .hlc-shop__quicklinks { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; min-width: 0; max-width: 100%; margin-top: 3px; }
        .hlc-shop__quicklinks span {
          font-size: 11px; font-weight: 800; letter-spacing: 0.12em; text-transform: uppercase;
          color: var(--hlc-text-secondary);
        }
        .hlc-shop__quicklinks button {
          background: var(--hlc-surface);
          border: 1px solid var(--hlc-border);
          color: #fff;
          font-size: 13px;
          font-weight: 600;
          padding: 8px 16px;
          border-radius: 999px;
          cursor: pointer;
          transition: border-color 0.2s ease, background 0.2s ease, color 0.2s ease;
        }
        .hlc-shop__quicklinks button:hover,
        .hlc-shop__quicklinks button.is-active {
          border-color: var(--hlc-magenta-light);
          background: rgba(155, 31, 108, 0.14);
          color: var(--hlc-magenta-light);
        }

        /* ---------- toolbar ---------- */
        .hlc-shop__toolbar {
          width: min(1360px, 94vw);
          margin: 0 auto 16px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 14px;
          flex-wrap: wrap;
        }
        .hlc-shop__count { font-size: 13px; color: var(--hlc-text-secondary); font-weight: 600; }
        .hlc-shop__count b { color: var(--hlc-magenta-light); font-weight: 800; }

        .hlc-shop__toolbar-right { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; max-width: 100%; }
        .hlc-shop__mobile-filters-btn {
          display: none;
          align-items: center;
          gap: 8px;
          background: var(--hlc-surface);
          border: 1px solid var(--hlc-border);
          color: #fff;
          font-size: 13px;
          font-weight: 700;
          padding: 9px 14px;
          border-radius: 8px;
          cursor: pointer;
        }
        .hlc-shop__mobile-filters-btn svg { width: 15px; height: 15px; }

        .hlc-shop__sort { display: flex; align-items: center; gap: 8px; min-width: 0; max-width: 100%; }
        .hlc-shop__sort label {
          font-size: 10.5px; font-weight: 800; letter-spacing: 0.1em; text-transform: uppercase;
          color: var(--hlc-text-secondary);
        }
        .hlc-shop__sort-wrap { position: relative; min-width: 0; }
        .hlc-shop__sort select { max-width: 100%; color-scheme: dark; }
        .hlc-shop__sort select option { background: var(--hlc-bg-secondary); color: #fff; }
        .hlc-shop__sort select {
          appearance: none; -webkit-appearance: none;
          background: var(--hlc-surface);
          border: 1px solid var(--hlc-border);
          color: #fff;
          font-size: 13px;
          font-weight: 600;
          padding: 9px 32px 9px 14px;
          border-radius: 8px;
          cursor: pointer;
        }
        .hlc-shop__sort-wrap svg {
          position: absolute; right: 10px; top: 50%; transform: translateY(-50%);
          width: 13px; height: 13px; color: var(--hlc-text-secondary); pointer-events: none;
        }

        /* ---------- layout ---------- */
        .hlc-shop__inner {
          width: min(1360px, 94vw);
          margin: 0 auto;
          display: grid;
          grid-template-columns: 240px 1fr;
          gap: 24px;
          align-items: start;
          padding-bottom: clamp(32px, 4vw, 48px);
        }

        /* ---------- filters ---------- */
        .hlc-shop__filters {
          background: var(--hlc-surface);
          border: 1px solid var(--hlc-border);
          border-radius: var(--hlc-radius-sm);
          padding: 18px;
          position: sticky;
          top: 90px;
        }
        .hlc-shop__filters-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 14px; }
        .hlc-shop__filters-head h3 {
          margin: 0; font-size: 12px; font-weight: 800; text-transform: uppercase;
          letter-spacing: 0.12em; color: #fff;
        }
        .hlc-shop__filters-close { display: none; background: none; border: none; color: #fff; cursor: pointer; padding: 4px; }
        .hlc-shop__filters-close svg { width: 18px; height: 18px; }

        .hlc-shop__filters label {
          font-size: 10.5px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em;
          color: var(--hlc-text-secondary); margin-top: 14px; display: block;
        }
        .hlc-shop__filters label:first-of-type { margin-top: 0; }

        .hlc-shop__select-wrap { position: relative; margin-top: 6px; }
        .hlc-shop__select-wrap select {
          width: 100%;
          appearance: none; -webkit-appearance: none;
          padding: 10px 32px 10px 12px;
          background: rgba(255,255,255,0.04);
          border: 1px solid var(--hlc-border);
          border-radius: 8px;
          font-size: 13px;
          color: #fff;
          cursor: pointer;
          transition: border-color 0.2s ease, background 0.2s ease;
        }
        .hlc-shop__select-wrap select:focus { outline: none; border-color: var(--hlc-magenta-light); }
        .hlc-shop__select-wrap select.has-value {
          border-color: rgba(155,31,108,0.5);
          background: rgba(155,31,108,0.08);
        }
        .hlc-shop__select-wrap select option { background: var(--hlc-bg-secondary); color: #fff; }
        .hlc-shop__select-wrap svg {
          position: absolute; right: 10px; top: 50%; transform: translateY(-50%);
          width: 13px; height: 13px; color: var(--hlc-text-secondary); pointer-events: none;
        }

        .filter-badge {
          display: inline-flex; align-items: center; justify-content: center;
          background: var(--hlc-magenta-light); color: #fff; font-size: 10px; font-weight: 700;
          width: 18px; height: 18px; border-radius: 50%; margin-left: 6px; vertical-align: middle;
        }

        .filter-actions { display: flex; gap: 10px; margin-top: 18px; }
        .filter-actions button {
          flex: 1; padding: 11px; font-weight: 700; font-size: 11.5px; letter-spacing: 0.03em;
          text-transform: uppercase; border-radius: 8px; cursor: pointer; transition: filter 0.2s ease, background 0.2s ease;
        }
        #applyFilters { background: var(--hlc-gradient-primary); color: #fff; border: none; }
        #applyFilters:hover { filter: brightness(1.12); }
        #clearFilters { background: transparent; color: #fff; border: 1px solid var(--hlc-border-strong); }
        #clearFilters:hover { background: rgba(255,255,255,0.06); }

        .hlc-shop__filters-scrim { display: none; }

        /* ---------- grid ---------- */
        .hlc-shop__grid {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 18px;
        }

        .hlc-prod-card {
          position: relative;
          display: flex;
          flex-direction: column;
          background: linear-gradient(165deg, var(--hlc-bg-secondary) 0%, var(--hlc-bg) 100%);
          border: 1px solid var(--hlc-border);
          border-radius: var(--hlc-radius-sm);
          overflow: hidden;
          cursor: pointer;
          transition: transform 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease;
        }
        .hlc-prod-card:hover {
          transform: translateY(-4px);
          border-color: rgba(155, 31, 108, 0.5);
          box-shadow: 0 20px 40px rgba(155, 31, 108, 0.22);
        }

        .hlc-prod-card__imgwrap {
          position: relative;
          aspect-ratio: 4 / 3;
          overflow: hidden;
        }
        .hlc-prod-card__imgwrap::before {
          content: "";
          position: absolute; inset: 10%;
          background: radial-gradient(circle at 50% 55%, rgba(155,31,108,0.18), transparent 70%);
          filter: blur(16px);
        }
        .hlc-prod-card__imgwrap img {
          position: relative;
          width: 100%; height: 100%;
          object-fit: contain;
          display: block;
          transition: transform 0.4s ease;
        }
        .hlc-prod-card:hover .hlc-prod-card__imgwrap img { transform: scale(1.045); }

        .hlc-prod-card__placeholder {
          width: 100%; height: 100%; display: flex; align-items: center; justify-content: center;
          color: var(--hlc-text-faint); font-size: 13px;
        }

        .hlc-prod-card__badge {
          position: absolute; top: 10px; left: 10px; z-index: 2;
          background: var(--hlc-gradient-primary);
          color: #fff; font-size: 10px; font-weight: 700; padding: 5px 9px;
          letter-spacing: 0.04em; border-radius: 4px;
        }

        .hlc-prod-card__qv {
          position: absolute; top: 10px; right: 10px; z-index: 2;
          width: 44px; height: 44px; border-radius: 50%;
          background: rgba(10,8,20,0.5);
          border: 1px solid var(--hlc-border);
          backdrop-filter: blur(6px);
          color: #fff;
          display: inline-flex; align-items: center; justify-content: center;
          cursor: pointer;
          transition: background 0.2s ease, border-color 0.2s ease, transform 0.2s ease;
        }
        .hlc-prod-card__qv svg { width: 15px; height: 15px; }
        .hlc-prod-card__qv:hover { background: var(--hlc-magenta-light); border-color: var(--hlc-magenta-light); transform: scale(1.06); }

        .hlc-prod-card__body {
          padding: 14px 14px 16px;
          flex: 1;
          display: flex;
          flex-direction: column;
        }
        .hlc-prod-card__title {
          margin: 0 0 3px;
          font-size: 14px;
          font-weight: 700;
          color: #fff;
          line-height: 1.3;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .hlc-prod-card__meta {
          margin: 0 0 8px;
          font-size: 10.5px;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          color: var(--hlc-text-faint);
        }
        .hlc-prod-card__price {
          font-size: 15px;
          font-weight: 800;
          color: var(--hlc-magenta-light);
          margin: 0 0 12px;
        }
        .hlc-prod-card__price del {
          color: var(--hlc-text-faint);
          font-weight: 400;
          margin-right: 6px;
          font-size: 12px;
        }

        .hlc-prod-card__cta-row { margin-top: auto; display: flex; flex-direction: column; gap: 8px; }

        .hlc-prod-card__addcart {
          width: 100%;
          display: inline-flex; align-items: center; justify-content: center; gap: 7px;
          min-height: 40px;
          background: var(--hlc-gradient-primary);
          color: #fff;
          border: none;
          border-radius: 6px;
          font-size: 12.5px;
          font-weight: 700;
          cursor: pointer;
          transition: filter 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
        }
        .hlc-prod-card__addcart svg { width: 14px; height: 14px; }
        .hlc-prod-card__addcart:hover { filter: brightness(1.12); transform: translateY(-1px); box-shadow: 0 8px 18px rgba(155,31,108,0.35); }

        .hlc-prod-card__counter {
          width: 100%;
          display: flex; align-items: center; justify-content: center; gap: 6px;
          min-height: 40px;
          background: rgba(255,255,255,0.05);
          border: 1px solid var(--hlc-border);
          border-radius: 6px;
        }
        .hlc-prod-card__counter button {
          width: 44px; height: 44px; border: none; border-radius: 4px;
          background: var(--hlc-gradient-primary); color: #fff; font-size: 14px; font-weight: 700; cursor: pointer;
        }
        .hlc-prod-card__counter span { font-size: 13px; font-weight: 700; color: #fff; min-width: 16px; text-align: center; }

        .hlc-prod-card__viewlink {
          background: none; border: none; padding: 0; cursor: pointer;
          text-align: center;
          font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em;
          color: var(--hlc-text-secondary);
          transition: color 0.2s ease;
        }
        .hlc-prod-card:hover .hlc-prod-card__viewlink,
        .hlc-prod-card__viewlink:hover { color: var(--hlc-magenta-light); }

        .hlc-shop__empty {
          grid-column: 1 / -1;
          text-align: center;
          padding: 60px 20px;
          color: var(--hlc-text-secondary);
          font-size: 15px;
        }

        .hlc-shop__loading {
          grid-column: 1 / -1;
          text-align: center;
          padding: 60px 20px;
          color: var(--hlc-text-secondary);
          font-size: 14px;
        }

        /* ---------- load more ---------- */
        .hlc-shop__loadmore { display: flex; justify-content: center; margin-top: 30px; }
        .hlc-shop__loadmore button {
          background: transparent;
          border: 1.6px solid var(--hlc-border-strong);
          color: #fff;
          padding: 13px 30px;
          font-size: 12.5px;
          font-weight: 700;
          letter-spacing: 0.05em;
          text-transform: uppercase;
          border-radius: 8px;
          cursor: pointer;
          transition: border-color 0.25s ease, background 0.25s ease;
        }
        .hlc-shop__loadmore button:hover {
          border-color: var(--hlc-magenta-light);
          background: rgba(155,31,108,0.1);
        }

        /* ---------- benefits strip ---------- */
        .hlc-shop__benefits {
          border-top: 1px solid var(--hlc-border);
          padding: 26px 0;
        }
        .hlc-shop__benefits-inner {
          width: min(1360px, 94vw);
          margin: 0 auto;
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 34px;
        }
        .hlc-shop__benefit { display: flex; align-items: center; gap: 10px; }
        .hlc-shop__benefit-icon {
          width: 34px; height: 34px; border-radius: 50%;
          display: inline-flex; align-items: center; justify-content: center;
          color: var(--hlc-magenta-light);
          background: rgba(155,31,108,0.1);
          border: 1px solid rgba(155,31,108,0.3);
          flex-shrink: 0;
        }
        .hlc-shop__benefit-icon svg { width: 16px; height: 16px; }
        .hlc-shop__benefit h4 { margin: 0; font-size: 12.5px; font-weight: 800; color: #fff; }
        .hlc-shop__benefit p { margin: 0; font-size: 11px; color: var(--hlc-text-secondary); }

        /* ---------- responsive ---------- */
        @media (max-width: 1200px) {
          .hlc-shop__grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
        }

        @media (max-width: 991px) {
          .hlc-shop__inner { grid-template-columns: 1fr; }
          .hlc-shop__mobile-filters-btn { display: inline-flex; }
          .hlc-shop__filters {
            position: fixed; top: 0; right: 0; bottom: 0; z-index: 1001;
            width: min(340px, 86vw); border-radius: 0; border: none;
            border-left: 1px solid var(--hlc-border);
            transform: translateX(100%);
            transition: transform 0.3s ease;
            overflow-y: auto;
          }
          .hlc-shop__filters.is-open { transform: translateX(0); background: var(--hlc-bg-secondary); }
          .hlc-shop__filters-close { display: inline-flex; align-items: center; }
          .hlc-shop__filters-scrim.is-open {
            display: block; position: fixed; inset: 0; z-index: 1000;
            background: rgba(0,0,0,0.6);
          }
          .hlc-shop__grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
        }

        @media (max-width: 767px) {
          .hlc-shop__grid { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; gap: 12px !important; }
          .hlc-prod-card__body { padding: 10px; }
          .hlc-prod-card__title { font-size: 12px; }
          .hlc-prod-card__meta { font-size: 9px; }
          .hlc-prod-card__price { font-size: 12.5px; margin-bottom: 8px; }
          .hlc-prod-card__addcart, .hlc-prod-card__counter { min-height: 36px; font-size: 11px; }
          .hlc-prod-card__viewlink { font-size: 9.5px; }
          .filter-actions button { font-size: 10.5px; padding: 10px 6px; }
          .hlc-shop__toolbar { justify-content: space-between; }
        }

        @media (max-width: 420px) {
          .hlc-shop__benefits-inner { gap: 18px; }
        }
      `}</style>

      <Navbar />
      <div style={{ marginTop: "var(--nav-h, 74px)" }} />

      <div id="hlc-shop-page" style={cssVars}>
        <section className="hlc-shop__hero">
          <div className="hlc-shop__hero-inner">
            <div className="hlc-shop__hero-main">
              <span className="hlc-shop__eyebrow">Shop HLC</span>
              <h1>Lighting designed for better spaces</h1>
              <p>
                Explore HLC's complete collection of lighting solutions for homes,
                commercial spaces, outdoor environments and modern projects.
              </p>
            </div>

            <div className="hlc-shop__quicklinks">
              <span>Browse:</span>
              <button type="button" className="is-active" onClick={() => navigate('/all-products')}>
                All Products
              </button>
              <button type="button" onClick={() => navigate('/discounted-products')}>
                Discounted Products
              </button>
            </div>
          </div>
        </section>

        <div className="hlc-shop__toolbar">
          <p className="hlc-shop__count">
            Showing <b>{visibleProducts.length}</b> of <b>{sortedProducts.length}</b> product
            {sortedProducts.length !== 1 ? 's' : ''}
          </p>

          <div className="hlc-shop__toolbar-right">
            <button type="button" className="hlc-shop__mobile-filters-btn" aria-expanded={mobileFiltersOpen} aria-controls="hlc-product-filters" onClick={() => setMobileFiltersOpen(true)}>
              <FilterIcon /> Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
            </button>

            <div className="hlc-shop__sort">
              <label htmlFor="shop_sort">Sort by</label>
              <div className="hlc-shop__sort-wrap">
                <select id="shop_sort" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                  {SORT_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <ChevronIcon />
              </div>
            </div>
          </div>
        </div>

        <div className="hlc-shop__inner">
          {/* ── FILTERS (desktop sidebar / mobile drawer) ── */}
          <div className={`hlc-shop__filters-scrim${mobileFiltersOpen ? ' is-open' : ''}`} onClick={() => setMobileFiltersOpen(false)} />
          <aside id="hlc-product-filters" ref={dialogRef} role={isModal && mobileFiltersOpen ? "dialog" : undefined} aria-hidden={isModal && !mobileFiltersOpen ? true : undefined} aria-modal={isModal && mobileFiltersOpen ? true : undefined} aria-label="Product filters" tabIndex={-1} inert={isModal && !mobileFiltersOpen ? true : undefined} className={`hlc-shop__filters${mobileFiltersOpen ? ' is-open' : ''}`}>
            {filterPanel}
          </aside>

          {/* ── PRODUCT GRID ── */}
          <main>
            <div className="hlc-shop__grid">
              {products.length === 0 ? (
                <div className="hlc-shop__loading">Loading products…</div>
              ) : visibleProducts.length === 0 ? (
                <div className="hlc-shop__empty">No products found. Try clearing filters.</div>
              ) : (
                visibleProducts.map((product, idx) => {
                  const image = getCutoutImage(product);
                  const cartItem = getCartItem(product.id);
                  return (
                    <article
                      key={product.id}
                      className="hlc-prod-card"
                      onClick={() => viewProduct(product.id)}
                    >
                      {resolveBadge(product) && (
                        <span className="hlc-prod-card__badge">{resolveBadge(product)}</span>
                      )}
                      <button
                        type="button"
                        className="hlc-prod-card__qv"
                        aria-label={`Quick view ${product.name}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          openModal(product);
                        }}
                      >
                        <EyeIcon />
                      </button>

                      <div className="hlc-prod-card__imgwrap">
                        {image ? (
                          <img
                            src={image}
                            alt={product.name}
                            loading={idx < 8 ? 'eager' : 'lazy'}
                            decoding="async"
                          />
                        ) : (
                          <div className="hlc-prod-card__placeholder">No Image</div>
                        )}
                      </div>

                      <div className="hlc-prod-card__body">
                        <h3 className="hlc-prod-card__title" title={product.name}>{product.name}</h3>
                        {product.category && (
                          <p className="hlc-prod-card__meta">{product.category}</p>
                        )}
                        <div className="hlc-prod-card__price">
                          {product.discountedPrice && <del>Rs {product.price}</del>}
                          Rs {product.discountedPrice || product.price}
                        </div>

                        <div className="hlc-prod-card__cta-row">
                          {!cartItem ? (
                            <button
                              type="button"
                              className="hlc-prod-card__addcart"
                              onClick={(e) => {
                                e.stopPropagation();
                                dispatch(addToCart(product));
                              }}
                            >
                              <BagIcon /> Add to Cart
                            </button>
                          ) : (
                            <div className="hlc-prod-card__counter" onClick={(e) => e.stopPropagation()}>
                              <button type="button" aria-label={`Decrease quantity of ${product.name}`} onClick={() => dispatch(decrementQty(product.id))}>-</button>
                              <span>{cartItem.quantity || 1}</span>
                              <button type="button" aria-label={`Increase quantity of ${product.name}`} onClick={() => dispatch(incrementQty(product.id))}>+</button>
                            </div>
                          )}
                          <Link
                            to={`/product/${product.id}`}
                            aria-label={`View ${product.name}`}
                            className="hlc-prod-card__viewlink"
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                          >
                            View Product →
                          </Link>
                        </div>
                      </div>
                    </article>
                  );
                })
              )}
            </div>

            {hasMore && (
              <div className="hlc-shop__loadmore">
                <button type="button" onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}>
                  Load More Products
                </button>
              </div>
            )}
          </main>
        </div>

        <div className="hlc-shop__benefits">
          <div className="hlc-shop__benefits-inner">
            <div className="hlc-shop__benefit">
              <span className="hlc-shop__benefit-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2l8 3v6c0 5-3.4 9-8 11-4.6-2-8-6-8-11V5z" /><path d="M9 12l2 2 4-4" /></svg>
              </span>
              <div><h4>1-Year Warranty</h4><p>Peace of mind, always.</p></div>
            </div>
            <div className="hlc-shop__benefit">
              <span className="hlc-shop__benefit-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M11 3C7 5 5 8 5 12a7 7 0 0 0 14 0c0-1.5-.5-3-1.5-4.5C16 9 14 9 13 7c-.8-1.5-1-2.7-2-4z" /></svg>
              </span>
              <div><h4>Energy Efficient</h4><p>Save up to 85% electricity.</p></div>
            </div>
            <div className="hlc-shop__benefit">
              <span className="hlc-shop__benefit-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M2 7h11v8H2z" /><path d="M13 10h4l3 3v2h-7z" /><circle cx="6" cy="18" r="1.7" /><circle cx="17" cy="18" r="1.7" /></svg>
              </span>
              <div><h4>Nationwide Delivery</h4><p>Fast &amp; reliable across Pakistan.</p></div>
            </div>
            <div className="hlc-shop__benefit">
              <span className="hlc-shop__benefit-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M4 13a8 8 0 0 1 16 0" /><path d="M4 13v4a2 2 0 0 0 2 2h1v-6H5a1 1 0 0 0-1 1z" /><path d="M20 13v4a2 2 0 0 1-2 2h-1v-6h1a1 1 0 0 1 2 1z" /><path d="M14 19a2 2 0 0 1-4 0" /></svg>
              </span>
              <div><h4>Dedicated Support</h4><p>We're here to help you.</p></div>
            </div>
          </div>
        </div>
      </div>

      <HlcFooter />

      <QuickViewModal
        product={selectedProduct}
        open={modalOpen}
        onClose={closeModal}
        cartItem={selectedProduct ? getCartItem(selectedProduct.id) : null}
        onAddToCart={(p) => dispatch(addToCart(p))}
        onIncrement={(pid) => dispatch(incrementQty(pid))}
        onDecrement={(pid) => dispatch(decrementQty(pid))}
        onViewFull={viewProduct}
      />
    </>
  );
}
